import React from "react"
import { useNavigate } from "react-router-dom"
import { Apple, Smartphone } from "lucide-react"
import { Button } from "@/shared/ui"
import { BottomDrawer } from "@/shared/ui/BottomDrawer"
import { buildWhatsAppLink } from "@/shared/utils/whatsapp"
import { SUPPORT_WHATSAPP_E164 } from "@/shared/data/support"
import { syncSubscriptionStatus } from "@/modules/plans/services/memberPlanService"
import { clearPendingPlanChange, getPendingPlanChange } from "@/modules/plans/utils/pendingPlanChange"
import obrigadoVideoSrc from "@/images/video/video-obrigado-app-vertical.mp4"
import iosTutorialVideoSrc from "@/images/video/video-tutorial-ios.mp4"
import androidTutorialVideoSrc from "@/images/video/video-tutorial-android.mp4"

export default function ThankYouPage() {
  const navigate = useNavigate()

  const waMessage =
    "Ola! Fiz a assinatura do Cripto Alert e agora preciso de ajuda para instalar o app"
  const waUrl = buildWhatsAppLink(SUPPORT_WHATSAPP_E164, waMessage)

  const [openIos, setOpenIos] = React.useState(false)
  const [openAndroid, setOpenAndroid] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      const pending = getPendingPlanChange()
      if (!pending?.subscriptionId) return
      try {
        const status = await syncSubscriptionStatus(pending.subscriptionId)
        if (!cancelled && status && ["active", "trialing"].includes(status.toLowerCase())) {
          clearPendingPlanChange()
        }
      } catch {}
    })()
    return () => { cancelled = true }
  }, [])

  return (
    <div className="mx-auto w-full max-w-[960px] pb-12 px-4">
      <div className="mt-[60px] mb-8 text-center">
        <h1 className="text-2xl font-semibold">Obrigado pela assinatura!</h1>
        <p className="mt-2 text-sm text-foreground/70">
          Em poucos minutos voce podera instalar o app do Cripto Alert no seu
          celular.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] items-start">
        <div className="rounded-xl border border-border bg-muted/20 p-4 flex flex-col items-center justify-center">
          <div className="w-full max-w-[320px] md:max-w-[360px]">
            <div className="h-[500px] md:h-[637px] w-full overflow-hidden rounded-xl bg-black/80">
              <video
                src={obrigadoVideoSrc}
                className="h-full w-full object-cover"
                playsInline
                muted
                controls
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-muted/20 p-4 text-sm leading-6">
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <Apple className="h-4 w-4" />
              Instalacao em iPhone (iOS)
            </h2>
            <p className="text-xs text-foreground/70">
              Este card e exclusivo para quem usa iPhone (iOS). Clique no botao
              abaixo para ver o passo a passo completo de instalacao do app no
              seu aparelho.
            </p>
            <Button
              variant="outline"
              className="mt-3 w-full"
              onClick={() => setOpenIos(true)}
            >
              Ver passo a passo iOS
            </Button>
          </div>

          <div className="rounded-xl border border-border bg-muted/20 p-4 text-sm leading-6">
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <Smartphone className="h-4 w-4" />
              Instalacao em Android
            </h2>
            <p className="text-xs text-foreground/70">
              Este card e exclusivo para quem usa celular Android. Clique no
              botao abaixo para ver o passo a passo completo de instalacao do
              app no seu aparelho.
            </p>
            <Button
              variant="outline"
              className="mt-3 w-full"
              onClick={() => setOpenAndroid(true)}
            >
              Ver passo a passo Android
            </Button>
          </div>

          <div className="pt-2 space-y-2">
            <Button
              className="w-full"
              onClick={() => navigate("/login")}
            >
              Acessar painel
            </Button>
            <Button asChild variant="outline" className="w-full">
              <a href={waUrl} target="_blank" rel="noopener noreferrer">
                Falar com suporte no WhatsApp
              </a>
            </Button>
          </div>
        </div>
      </div>

      <BottomDrawer
        open={openIos}
        onOpenChange={setOpenIos}
        title="Instalacao em iPhone (iOS)"
        className="top-0 bottom-0 max-h-screen"
      >
        <div className="space-y-4 text-sm">
          <div className="w-full max-w-[420px] mx-auto">
            <div className="w-full overflow-hidden rounded-xl bg-black/80">
              <video
                src={iosTutorialVideoSrc}
                className="h-full w-full object-cover"
                playsInline
                controls
              />
            </div>
          </div>
          <p className="text-foreground/70">
            Use este video como guia para instalar o app do Cripto Alert no seu
            iPhone. Se precisar, voce pode pausar ou assistir novamente.
          </p>
          <Button
            variant="outline"
            className="mt-4 w-full"
            onClick={() => setOpenIos(false)}
          >
            Voltar para a pagina de obrigado
          </Button>
        </div>
      </BottomDrawer>

      <BottomDrawer
        open={openAndroid}
        onOpenChange={setOpenAndroid}
        title="Instalacao em Android"
        className="top-0 bottom-0 max-h-screen"
      >
        <div className="space-y-4 text-sm">
          <div className="w-full max-w-[420px] mx-auto">
            <div className="w-full overflow-hidden rounded-xl bg-black/80">
              <video
                src={androidTutorialVideoSrc}
                className="h-full w-full object-cover"
                playsInline
                controls
              />
            </div>
          </div>
          <p className="text-foreground/70">
            Use este video como guia para instalar o app do Cripto Alert no seu
            celular Android. Se precisar, voce pode pausar ou assistir
            novamente.
          </p>
          <Button
            variant="outline"
            className="mt-4 w-full"
            onClick={() => setOpenAndroid(false)}
          >
            Voltar para a pagina de obrigado
          </Button>
        </div>
      </BottomDrawer>
    </div>
  )
}
